import React from "react";
import { useParams, NavLink, useHistory } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";

import LoadingIndicator from "../../components/LoadingIndicator";
import useFetchCategory from "../../hooks/useFetchCategory";
import { useTaskFilters } from "../../context/TaskFilterContext";
import { groupTasks, sortTasks } from "../../utils/taskGroupingAndSorting";
import { truncateText } from "../../utils/textUtils";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import useTaskDeletion from "../../hooks/useTaskDeletion";
import { useRedirect } from "../../hooks/useRedirect";

import styles from "../../styles/pages/CategoryPage.module.css";
import btnStyles from "../../styles/general/Button.module.css";

const CategoryPage = () => {
  const history = useHistory();
  useRedirect("loggedOut");
  const { id } = useParams();
  const { category, tasks, hasLoaded, refreshCategory } = useFetchCategory(id);
  const { groupBy, sortBy, order } = useTaskFilters();
  const {
    showDeleteModal,
    taskToDelete,
    setShowDeleteModal,
    handleDeleteClick,
    handleConfirmDelete,
  } = useTaskDeletion(refreshCategory);

  // Apply grouping & sorting logic
  const groupedTasks = groupTasks(tasks, groupBy).map(({ group, tasks }) => ({
    group,
    tasks: [...tasks].sort((a, b) => sortTasks(a, b, sortBy, order)), // Sort tasks inside groups
  }));

  return (
    <Container fluid className={styles.CategoryContainer}>
      {!hasLoaded ? (
        <LoadingIndicator spinner message="Loading category..." />
      ) : category ? (
        <>
          {/* Delete Confirmation Modal */}
          <DeleteConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleConfirmDelete}
            taskTitle={taskToDelete?.title}
          />

          {/* Back Button */}
          <Row className={styles.BackButtonRow}>
            <Col className={styles.BackButtonCol}>
              <Button
                variant="link"
                aria-label="Back Button"
                className={btnStyles.BackIcon}
                onClick={() => window.history.back()}
              >
                <i className="fa-regular fa-circle-left"></i>
              </Button>
            </Col>
          </Row>

          {/* Category Title */}
          <Row className={styles.TitleRow}>
            <Col className={styles.TitleCol}>
              <h1 className={styles.Title}>{category.name}</h1>
            </Col>
          </Row>

          {/* Category Details */}
          <Row className={styles.DetailsRow}>
            <Col className={styles.DetailsCol}>
              <p>
                <strong>Task Count:</strong> {category.task_count}
              </p>
            </Col>
          </Row>

          {/* Tasks List */}
          {tasks.length === 0 ? (
            <p className={styles.NoTasks}>No tasks in this category.</p>
          ) : (
            groupedTasks.map(({ group, tasks }) => (
              <div key={group} className={styles.GroupContainer}>
                <h2 className={styles.GroupHeading}>{group}</h2>
                <Row className={styles.TaskRow}>
                  {tasks.map((task) => (
                    <Col
                      key={task.id}
                      lg={4}
                      md={6}
                      xs={12}
                      className={styles.TaskCol}
                    >
                      <NavLink
                        to={`/tasks/${task.id}`}
                        className={styles.TaskLink}
                      >
                        <Card className={styles.TaskCard}>
                          <div className={styles.ButtonContainer}>
                            <Button
                              className={btnStyles.EditButton}
                              aria-label="Edit Task"
                              onClick={(e) => {
                                e.preventDefault();
                                history.push(`/tasks/${task.id}/edit`);
                              }}
                            >
                              <i className="fa-solid fa-pen-to-square"></i>
                            </Button>
                            <Button
                              aria-label="Delete Task"
                              className={btnStyles.DeleteButton}
                              onClick={(e) => handleDeleteClick(e, task)}
                            >
                              <i className="fa-solid fa-trash-can"></i>
                            </Button>
                          </div>
                          <Card.Body className={styles.TaskCardBody}>
                            <Card.Title className={styles.TaskTitle}>
                              {truncateText(task.title, 15)}
                            </Card.Title>
                            <Card.Text className={styles.TaskDetails}>
                              <span className={styles.TaskDueDate}>
                                Due Date: {task.due_date || "N/A"}
                              </span>
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </NavLink>
                    </Col>
                  ))}
                </Row>
              </div>
            ))
          )}
        </>
      ) : (
        <p className={styles.NotFound}>Category not found.</p>
      )}
    </Container>
  );
};

export default CategoryPage;
